import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type { RecordingResult } from './api';

export type StoredRecording = RecordingResult & {
  fileExtension: string;
  /** 'recording' rows are written at start; one left behind by a closed tab is adopted by recovery. */
  state: 'recording' | 'complete';
  /** Last time the row was touched; the liveness fallback when Web Locks are unavailable. */
  updatedAt: number;
  recovered?: boolean;
};

interface RecordingsDb extends DBSchema {
  recordings: { key: string; value: StoredRecording };
}

/** Separate from the project database so recorder metadata never competes for schema upgrades with it. */
export function createRecordingMetaStore() {
  let pending: Promise<IDBPDatabase<RecordingsDb>> | null = null;
  const open = () => {
    pending ??= openDB<RecordingsDb>('ohmyobs-recordings', 1, {
      upgrade(db) {
        db.createObjectStore('recordings', { keyPath: 'id' });
      },
      blocking() {
        void pending?.then((db) => db.close());
        pending = null;
      },
      terminated() {
        pending = null;
      },
    }).catch((err) => {
      pending = null;
      throw err;
    });
    return pending;
  };
  return {
    async put(rec: StoredRecording): Promise<void> {
      await (await open()).put('recordings', rec);
    },
    async list(): Promise<StoredRecording[]> {
      return (await open()).getAll('recordings');
    },
    async get(id: string): Promise<StoredRecording | undefined> {
      return (await open()).get('recordings', id);
    },
    async delete(id: string): Promise<void> {
      await (await open()).delete('recordings', id);
    },
  };
}
